// Copyright 2019 Stanford University see LICENSE for license
import Config from "Config"
/* eslint-disable n/no-unpublished-import */
import {
  getFixtureTemplateSearchResults,
  hasFixtureResource,
  resourceSearchResults,
} from "../__tests__/testUtilities/fixtureLoaderHelper"

/* eslint-enable n/no-unpublished-import */

// Not using ES client because not intended for use in browser.
/**
 * Performs a search of Sinopia resources.
 * @param {string} query
 * @param {Object} options for the search (resultsPerPage, startOfRange, sortField, sortOrder, typeFilter)
 * @return {Promise<Object>} promise containing the result of the search.
 */
export const getSearchResults = async (query, options = {}) =>
  getSearchResultsWithFacets(query, { ...options, noFacetResults: true }).then(
    ([results]) => results,
  )

// Always sends the page size and offset. Left out, the API returns its own
// default page (20 results from offset 0), so every page showed the same
// results and the pager's page count, based on resultsPerPage, did not match
// what was on screen.
const resourceSearchUrl = (query, options) => {
  const params = new URLSearchParams()
  if (query) params.set("q", query)
  params.set("limit", options.resultsPerPage || Config.searchResultsPerPage)
  params.set("offset", options.startOfRange || 0)
  return `${Config.sinopiaApiBase}/search/?${params}`
}

/**
 * Performs a search of Sinopia resources.
 * @param {string} query
 * @param {Object} options for the search (resultsPerPage, queryFrom, sortField, sortOrder, typeFilter, noFacetResults)
 * @return {Promise<Object>} promise containing the result of the search.
 */
export const getSearchResultsWithFacets = async (
  query,
  options = {},
  keycloak,
) => {
  if (Config.useResourceTemplateFixtures && hasFixtureResource(query))
    return Promise.resolve(resourceSearchResults(query))

  const url = resourceSearchUrl(query, options)

  // const termsFilters = []
  // if (options.typeFilter) {
  //   termsFilters.push({
  //     terms: {
  //       type: Array.isArray(options.typeFilter)
  //         ? options.typeFilter
  //         : [options.typeFilter],
  //     },
  //   })
  // }
  // if (options.groupFilter) {
  //   termsFilters.push({
  //     terms: {
  //       group: Array.isArray(options.groupFilter)
  //         ? options.groupFilter
  //         : [options.groupFilter],
  //     },
  //   })
  // }
  // if (!_.isEmpty(termsFilters)) body.query.bool.filter = termsFilters

  // if (!options.noFacetResults) {
  //   body.aggs = {
  //     types: {
  //       terms: {
  //         field: "type",
  //       },
  //     },
  //     groups: {
  //       terms: {
  //         field: "group",
  //         size: 20,
  //       },
  //     },
  //   }
  // }
  return fetchSearchResultsFromUrl(url, keycloak, options)
}

export const getSearchResultsByUris = (resourceUris) => {
  if (
    Config.useResourceTemplateFixtures &&
    resourceUris.length === 1 &&
    hasFixtureResource(resourceUris[0])
  )
    return Promise.resolve(resourceSearchResults(resourceUris[0])[0])

  /*
   * BUG: `body` below is built but never sent. fetchSearchResultsFromUrl issues
   * a bare GET, so `resourceUris` is discarded and this returns the first hit of
   * an unfiltered search rather than the requested URIs. Callers affected:
   * actionCreators/history.js (loadResourceHistory, addResourceHistory).
   * Retained rather than deleted because it documents the intended query shape;
   * fixing it requires the Blue Core API's search contract for filtering by URI.
   */
  // eslint-disable-next-line no-unused-vars
  const body = {
    query: {
      terms: {
        uri: resourceUris,
      },
    },
    size: resourceUris.length,
  }
  const url = `${Config.sinopiaApiBase}/search/`
  return fetchSearchResultsFromUrl(url, null).then((results) => results[0])
}

const fetchSearchResultsFromUrl = (url, keycloak, options) =>
  fetch(url, {
    method: "GET",
  })
    .then((resp) => resp.json())
    .then((json) => {
      if (json.error) {
        return [
          {
            totalHits: 0,
            results: [],
            error: json.error.reason || json.error,
            options,
          },
          undefined,
        ]
      }
      const result = hitsToResult(json)
      result.options = options
      return [result, aggregationsToResult(json)]
    })
    .catch((err) => [
      {
        totalHits: 0,
        results: [],
        error: err.toString(),
        options,
      },
      undefined,
    ])

const hitsToResult = (payload) => {
  const results = []
  payload.results.forEach((hit) => {
    const types = hit.data["@type"]
    const rdfTypes = []
    if (Array.isArray(types)) {
      types.forEach((type) =>
        rdfTypes.push(`http://id.loc.gov/ontologies/bibframe/${type}`),
      )
    } else {
      rdfTypes.push(`http://id.loc.gov/ontologies/bibframe/${types}`)
    }
    // Extract label from JSON-LD format if needed
    // title may be a single object or an array of typed title objects
    let titleObj = hit.data.title
    if (Array.isArray(titleObj)) {
      titleObj = titleObj.find((t) => t["@type"] === "Title") || titleObj[0]
    }
    const mainTitle = titleObj?.mainTitle
    let label
    if (Array.isArray(mainTitle)) {
      const first = mainTitle.find((m) => typeof m === "string") ?? mainTitle[0]
      label = first?.["@value"] ?? first
    } else {
      label = mainTitle?.["@value"] ?? mainTitle
    }

    const contributions = hit.data.contribution
    let primaryContributor
    if (Array.isArray(contributions)) {
      const primary = contributions.find(
        (c) => c["@type"] === "PrimaryContribution",
      )
      const agentLabel = primary?.agent?.label
      if (agentLabel !== undefined) {
        if (Array.isArray(agentLabel)) {
          const first =
            agentLabel.find((l) => typeof l === "string") ?? agentLabel[0]
          primaryContributor = first?.["@value"] ?? first
        } else {
          primaryContributor = agentLabel?.["@value"] ?? agentLabel
        }
      }
    }

    results.push({
      uri: hit.uri,
      label,
      ...(primaryContributor !== undefined && { primaryContributor }),
      created: hit.created_at,
      modified: hit.updated_at,
      type: rdfTypes,
      group: Config.defaultGroup,
      editGroups: [Config.defaultGroup],
    })
  })
  return {
    totalHits: payload.total,
    results,
  }
}

const aggregationsToResult = (aggs) => {
  if (!aggs) return undefined
  const result = {}
  Object.keys(aggs).forEach((field) => {
    if (aggs[field] && aggs[field].buckets) {
      result[field] = aggs[field].buckets
    }
  })
  return result
}

export const getTemplateSearchResults = (query, options = {}) => {
  const params = getTemplateSearchParams(query, options)
  return fetchTemplateSearchResults(params, templateHitsToResult).then(
    (searchResults) => {
      if (Config.useResourceTemplateFixtures) {
        const newResults = searchResults.results.filter(
          (hit) =>
            [hit.id, hit.resourceURI].includes(query) || query.length === 0,
        )
        return {
          totalHits: newResults.length,
          results: newResults,
          error: undefined,
        }
      }
      return searchResults
    },
  )
}

const getTemplateSearchParams = (query, options) => {
  const params = new URLSearchParams()
  if (query) {
    params.append("q", query)
  }
  const limit = options?.resultsPerPage || Config.templateSearchResultsPerPage
  const offset = options?.startOfRange || 0
  params.append("limit", limit)
  params.append("offset", offset)
  if (options?.excludeNested) {
    params.append("nested", "exclude")
  }
  return params
}

export const getTemplateSearchResultsByIds = (templateIds) => {
  const params = new URLSearchParams()
  // Blue Core API doesn't support 'id' parameter, use 'q' for query instead
  // This returns multiple results, so we need to filter by id after
  templateIds.forEach((id) => params.append("q", id))
  params.append("limit", 50) // Increase limit to ensure we get all results
  return fetchTemplateSearchResults(params, templateHitsToResult).then(
    (searchResults) => {
      // Filter results to only include templates with matching IDs
      const newResults = searchResults.results.filter((hit) =>
        templateIds.includes(hit.id),
      )
      return {
        totalHits: newResults.length,
        results: newResults,
        error: searchResults.error,
      }
    },
  )
}

const fetchTemplateSearchResults = async (params, hitsToResultFunc) => {
  if (Config.useResourceTemplateFixtures) {
    const results = await getFixtureTemplateSearchResults()
    return {
      totalHits: results.length,
      results,
      error: undefined,
    }
  }

  const url = `${Config.sinopiaApiBase}/search/profile?${params}`
  return fetch(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  })
    .then((resp) => {
      if (resp.status >= 300) {
        return {
          totalHits: 0,
          results: [],
          error: `${resp.status}: ${resp.statusText}`,
        }
      }
      return resp.json()
    })
    .then((json) => {
      if (json.error) {
        return {
          totalHits: 0,
          results: [],
          error: json.error.reason || json.error,
        }
      }
      return hitsToResultFunc(json)
    })
    .catch((err) => ({
      totalHits: 0,
      results: [],
      error: err.toString(),
    }))
}

const templateModFromBlueCore = (hit) => {
  // Formats Blue Core Template Result for editor's expectations
  let resourceAuthor = "Unknown"
  let resourceId = "Unknown"
  let resourceDate = "Unknown"
  let resourceLabel = "Unknown"
  let resourceRemark = ""
  let resourceURI = "Unknown"
  // Every field is optional: a profile created outside Sinopia's own template
  // editor may omit any of them. Fall back to the defaults above rather than
  // throwing -- an unguarded read here takes out the whole result set, not
  // just the offending row.
  hit.data.forEach((row) => {
    if (row["@id"] === hit.uri) {
      resourceAuthor =
        row["http://sinopia.io/vocabulary/hasAuthor"]?.[0]?.["@value"] ??
        resourceAuthor
      resourceDate =
        row["http://sinopia.io/vocabulary/hasDate"]?.[0]?.["@value"] ??
        resourceDate
      resourceLabel =
        row["http://www.w3.org/2000/01/rdf-schema#label"]?.[0]?.["@value"] ??
        resourceLabel
      resourceId =
        row["http://sinopia.io/vocabulary/hasResourceId"]?.[0]?.["@value"] ??
        resourceId
      resourceURI =
        row["http://sinopia.io/vocabulary/hasClass"]?.[0]?.["@id"] ??
        resourceURI
      resourceRemark =
        row["http://sinopia.io/vocabulary/hasRemark"]?.[0]?.["@value"] ?? ""
    }
  })
  // The Profile's own uri is the fetchable URL -- the same one the edit path
  // uses via originalURI. The old `${sinopiaApiBase}/resources/${hit.id}` path
  // no longer resolves now that profiles live at /profiles/{uuid}.
  const bcURI = hit.uri
  return {
    author: resourceAuthor,
    date: resourceDate,
    group: Config.defaultGroup,
    id: resourceId,
    originalURI: hit.uri,
    remark: resourceRemark,
    resourceLabel,
    resourceURI,
    uri: bcURI,
  }
}

const templateHitsToResult = (payload) => ({
  totalHits: payload.total || payload.results?.length || 0,
  results: (payload.results || []).map((row) => templateModFromBlueCore(row)),
  links: payload.links,
})

const templateLookupToResult = (payload) => ({
  totalHits: payload.total || payload.results?.length || 0,
  results: (payload.results || []).map((row) => {
    const template = templateModFromBlueCore(row)
    return {
      ...template,
      label: `${template.resourceLabel} (${template.id})`,
    }
  }),
  links: payload.links,
})

const getTemplateLookupResults = (query, options = {}) => {
  const params = getTemplateSearchParams(query, options)
  return fetchTemplateSearchResults(params, templateLookupToResult)
}

export const getLookupResult = (query, lookupConfig, options) => {
  // Templates get special handling since use id rather than URI.
  let getSearchResultsPromise
  if (lookupConfig.uri === "urn:ld4p:sinopia:resourceTemplate") {
    getSearchResultsPromise = getTemplateLookupResults(query, options)
  } else {
    getSearchResultsPromise = getSearchResults(query, {
      ...options,
      typeFilter: lookupConfig.type,
    })
  }
  return getSearchResultsPromise
}
